import { MouseEvent, ReactElement, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import cn from 'classnames';

import { FRAME_RATIO, containerToFrameY } from '../frameMapping';
import styles from './HotspotDebugOverlay.module.scss';

export const HOTSPOT_DEBUG_COOKIE_NAME = 'debugHotSpot';
/** Persists the on/off state of the crosshair overlay itself, independent of
    the `debugHotSpot` master cookie above, so toggling it off to interact
    with the image normally survives a page reload/navigation instead of
    resetting to "on" every time. */
export const HOTSPOT_DEBUG_VISIBLE_COOKIE_NAME = 'debugHotSpotVisible';
/** Matches `storepc`'s (`pages/_app.tsx`) expiry for a persisted UI
    preference cookie. */
const VISIBLE_COOKIE_EXPIRES_DAYS = 365;

/** How long the "Copied!" status replaces the live coordinate label. */
const COPIED_STATUS_MS = 1500;

export interface HotspotDebugOverlayProps {
  /** Which device's `hotspot_N_{device}_position` field this maps to. */
  device: 'desktop' | 'mobile';
}

interface CursorState {
  /** Container-relative px, for positioning the floating label. */
  left: number;
  top: number;
  /** Image-relative percentage (0-100, rounded to 1 decimal), combined into
      the `x/y` string the matching `hotspot_N_{device}_position` Prismic
      field expects (see `imageHotspot.schema.md`). */
  position: string;
}

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, value));
const round1 = (value: number): number => Math.round(value * 10) / 10;

/** `y` is converted back to frame space (`frameMapping.ts`) so a coordinate
    copied while the image is height-capped/cropped — e.g. on a wide monitor
    — still lands on the same point of the photo at every other viewport. */
const readPosition = (
  event: MouseEvent<HTMLDivElement>,
  device: HotspotDebugOverlayProps['device']
): Pick<CursorState, 'left' | 'top' | 'position'> => {
  const rect = event.currentTarget.getBoundingClientRect();
  const x = round1(
    clampPercent(((event.clientX - rect.left) / rect.width) * 100)
  );
  const y = round1(
    containerToFrameY(event.clientY - rect.top, rect, FRAME_RATIO[device])
  );

  return {
    left: event.clientX - rect.left,
    top: event.clientY - rect.top,
    position: `${x}/${y}`,
  };
};

/**
 * Merch/content-authoring aid, not shopper-facing UI: gated behind the
 * `debugHotSpot` cookie (same "set a cookie to `true`" pattern as
 * `getSPUMessaging.ts`'s `SPU_DEBUG`), it overlays the cursor's position over
 * the hotspot image as an `x/y` percentage string while hovering — exactly
 * the value the matching `hotspot_N_{device}_position` Prismic field expects
 * — and clicking copies that string to the clipboard, so an editor can read
 * coordinates off the photo and paste them straight in instead of the "trial
 * and error... 5+ attempts" preview loop described on ED-345. Text is
 * intentionally not run through next-i18next: it's only ever seen by an
 * editor who's opted into a debug cookie, never by a shopper.
 *
 * A top-left toggle (or the Escape key) lets the editor turn the crosshair
 * off — e.g. to click a hotspot dot normally — without losing debug mode
 * itself (the `debugHotSpot` cookie stays set); its own on/off choice
 * persists across page loads via `HOTSPOT_DEBUG_VISIBLE_COOKIE_NAME`,
 * defaulting to visible.
 *
 * Captures pointer events across the whole image while the crosshair is
 * visible (so hotspot dots/tiles underneath are briefly unclickable) —
 * acceptable since this is an explicit, opt-in debug mode, not a normal
 * render path; toggling it off restores normal clicking.
 */
const HotspotDebugOverlay = ({
  device,
}: HotspotDebugOverlayProps): ReactElement | null => {
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [cursor, setCursor] = useState<CursorState | null>(null);
  const [copiedPosition, setCopiedPosition] = useState<string | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setEnabled(Cookies.get(HOTSPOT_DEBUG_COOKIE_NAME) === 'true');
    setVisible(Cookies.get(HOTSPOT_DEBUG_VISIBLE_COOKIE_NAME) !== 'false');
  }, []);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    },
    []
  );

  const handleMouseLeave = () => {
    setCursor(null);
    setCopiedPosition(null);
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }
  };

  const handleToggle = () => {
    const next = !visible;
    setVisible(next);
    Cookies.set(HOTSPOT_DEBUG_VISIBLE_COOKIE_NAME, String(next), {
      expires: VISIBLE_COOKIE_EXPIRES_DAYS,
    });
    if (!next) {
      handleMouseLeave();
    }
  };

  // Lets an editor dismiss the crosshair from the keyboard — e.g. right after
  // clicking to copy a coordinate — without reaching for the toggle button.
  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleToggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleToggle closes over `visible` and is redefined every render; re-subscribing each render is cheap for this opt-in debug overlay, so `visible` alone is enough to keep the listener current.
  }, [enabled, visible]);

  if (!enabled) {
    return null;
  }

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    setCursor(readPosition(event, device));
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const { position } = readPosition(event, device);

    navigator.clipboard
      .writeText(position)
      .then(() => {
        setCopiedPosition(position);
        if (copiedTimeoutRef.current) {
          clearTimeout(copiedTimeoutRef.current);
        }
        copiedTimeoutRef.current = setTimeout(
          () => setCopiedPosition(null),
          COPIED_STATUS_MS
        );
      })
      // Clipboard access can be denied (permissions, insecure context) — the
      // live label still shows the value, so it's copyable by hand.
      .catch(() => {});
  };

  return (
    <>
      <button
        type="button"
        className={styles.toggle}
        onClick={handleToggle}
        aria-pressed={visible}
      >
        {visible ? 'Coords: on' : 'Coords: off'}
      </button>
      {visible && (
        <div
          className={styles.overlay}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          data-testid="hotspot-debug-overlay"
        >
          {cursor && (
            <span
              className={cn(styles.label, {
                [styles.labelCopied]: copiedPosition,
              })}
              style={{ left: cursor.left, top: cursor.top }}
            >
              {copiedPosition
                ? `Copied ${copiedPosition} to clipboard`
                : `${device} — ${cursor.position}`}
            </span>
          )}
        </div>
      )}
    </>
  );
};

export default HotspotDebugOverlay;
